// Was die Akte der Anlage rechnet (E3, S5; Mockup /crm/anlage/:id) – reine
// Logik, ohne React: seit E3c (S2) die Worte der Leiste (die Kette der
// Anlage; bis dahin der Weg durch die acht Kisten), was zur Dachplanung zu
// sagen ist, und die Anzahlen einer Besichtigung in einem Satz; seit E3b dazu der Vorschlag für Vor- und Nachname beim Berichtigen des Kunden. Woran eine Frage hängt, steht seit S6 in ../fragen/fragen.ts
// (`ortText`) – eine Stelle für Akte, Freigabe und die Seite „Fragen“; die
// Art eines Objekts in Worten in ../anzeige.ts (`objektArtName`).

import {
  KISTEN,
  namensVorschlag,
  PRODUKTLINIEN,
  SCHRITT_STAND_WORTE,
  type Akte,
  type AkteAufnahme,
  type Kette,
  type KettenSchritt,
  type SchrittInDerKette,
} from "@happy-energy/kern";
import { spaeterAls } from "../tage.ts";

// --- Namen -------------------------------------------------------------------------------------

/** Die Produktlinie kurz, für den Chip: „sun“ → „Sun“ (aus „Happy Energy Sun“, daten/produktlinien.json). */
export function produktlinieKurz(schluessel: string): string {
  const name = PRODUKTLINIEN.find((p) => p.schluessel === schluessel)?.name ?? schluessel;
  return name.replace(/^Happy Energy\s+/, "");
}

/** Der Name einer Kiste (daten/kisten.json) – unbekannt: der Schlüssel. */
export function kistenName(schluessel: string): string {
  return KISTEN.find((k) => k.schluessel === schluessel)?.name ?? schluessel;
}

// --- Kunde berichtigen: Vor- und Nachname (seit E3b, O82) --------------------------------------

type KundeName = Pick<Akte["kunde"], "name" | "vorname" | "nachname">;

/**
 * Vor- und Nachname fürs Formular: wie gespeichert – oder, solange der Name
 * nicht aufgeteilt ist (alle Kunden von vor E3b), der Vorschlag aus dem Kern
 * (letztes Wort = Nachname, „Familie“ fällt weg).
 *
 * Gespeichert wird, was in den Feldern steht – ohne Bremse und ohne Satz
 * darunter (Gernot, 23.10.2026). Die Bremse aus der Code-Prüfung E3b
 * (Befund 2: ein ungeteilter Name blieb ungeteilt, solange die Felder auf dem
 * Vorschlag standen) ist wieder heraus: Es gibt nur zwei ungeteilte echte
 * Kunden, Gernot teilt sie nach dem dritten Update selbst auf, und neue Kunden
 * kommen vom Gerät schon geteilt (der Nachname ist dort Pflicht). Die Bremse
 * verhinderte sonst das Aufteilen, wenn der Vorschlag schon stimmte
 * (Nachprüfung N2, reviews/2026-10-23-code-pruefung-e3b.md). Die Doku-App
 * behält ihre Bremse (arbeitskopie.ts, kundeAendern).
 */
export function namenZumAendern(k: KundeName): { vorname: string; nachname: string } {
  return k.nachname ? { vorname: k.vorname ?? "", nachname: k.nachname } : namensVorschlag(k.name);
}

// --- Die Leiste: die Kette der Anlage (seit E3c, S2) -------------------------------------------
// Bis E3c stand hier „der Weg der Anlage durch die acht Kisten“ – die Leiste
// sagt dasselbe genauer (je Schritt, was erledigt ist und was fehlt) und
// steht oben in der Akte; zwei Reihen mit je acht Kästchen hätten
// verwirrt (G14, G19).

/** Was ein Schritt sagt, wenn man darauf zeigt oder ihn antippt: „Bestellt: offen – noch offen: Module“. */
export function leisteText(s: Pick<SchrittInDerKette, "name" | "stand" | "grund" | "hinweis">): string {
  const teile = [`${s.name}: ${SCHRITT_STAND_WORTE[s.stand]}`];
  if (s.grund && s.grund !== SCHRITT_STAND_WORTE[s.stand]) teile.push(s.grund);
  const text = teile.join(" – ");
  return s.hinweis ? `${text}. ${s.hinweis}` : text;
}

/** Was man bei einem Schritt tut – für „Jetzt geht: …“ (die Namen der Leiste sagen, was erledigt ist). */
const TUN: Record<KettenSchritt, string> = {
  besichtigung: "die Besichtigung senden (draußen)",
  termin: "den Termin eintragen",
  dachplanung: "die Dachplanung (K2-Plan)",
  freigeben: "Freigeben",
  bestellt: "Bestellen",
  dachpartie: "die Dachpartie beauftragen",
  bodenpartie: "die Bodenpartie einteilen",
  montiert: "„montiert am“ eintragen",
};

/** Der Satz unter der Leiste, solange nichts angetippt ist: was jetzt geht – oder dass alles erledigt ist. */
export function leisteSatz(kette: Kette): string {
  if (kette.fertig) return "Alles erledigt – die Anlage ist montiert. Danach kommen Inbetriebnahme und Nachsorge.";
  const jetzt = kette.jetzt.map((s) => TUN[s]);
  const hinweise = kette.schritte.filter((s) => s.hinweis !== null).map((s) => s.name);
  const teile = [jetzt.length > 0 ? `Jetzt geht: ${jetzt.join(", ")}.` : "Gerade wartet alles auf einen Schritt davor."];
  if (hinweise.length > 0) teile.push(`Bitte ansehen: ${hinweise.join(", ")}.`);
  teile.push("Einen Schritt antippen – dann steht hier, warum.");
  return teile.join(" ");
}


// --- Dachplanung und Besichtigungen ----------------------------------------------------------

/** Wann das erste Mal gesendet wurde – davor ist die Dachplanung einfach die Dachplanung, danach „geändert“. */
export function ersteSendung(aufnahmen: readonly Pick<AkteAufnahme, "gesendetAm">[]): string | null {
  let erste: string | null = null;
  for (const a of aufnahmen) if (erste === null || spaeterAls(erste, a.gesendetAm)) erste = a.gesendetAm;
  return erste;
}

/**
 * Was zur Dachplanung dasteht: abgelegt, übersprungen oder noch keine – und
 * „geändert am …“, wenn sie sich nach dem ersten Senden geändert hat (ein
 * anderes PDF, oder entfernt; DECISIONS.md, 21.10.). Davor ist das Datum nur
 * das Ablegen beim Kunden und keine Änderung.
 */
export function dachplanung(k2: Akte["anlage"]["k2"], gesendetAm: string | null): { stand: "abgelegt" | "uebersprungen" | "offen"; satz: string; geaendertAm: string | null } {
  const geaendertAm = spaeterAls(k2.geaendertAm, gesendetAm) ? k2.geaendertAm : null;
  if (k2.pruefsumme !== null) return { stand: "abgelegt", satz: "K2-PDF samt Stückliste, beim Kunden abgelegt", geaendertAm };
  if (k2.uebersprungen) return { stand: "uebersprungen", satz: "Übersprungen – das PDF kann jederzeit nachkommen", geaendertAm };
  return { stand: "offen", satz: "Noch keine – das PDF kann jederzeit nachkommen", geaendertAm };
}

function anzahl(n: number, eins: string, mehr: string): string {
  return `${n} ${n === 1 ? eins : mehr}`;
}

/** Die Anzahlen einer Besichtigung in einem Satz: „7 von 7 Stationen · 15 Fotos · 16 Notizen · 3 Sprachmemos · 6 Längen“ – was null ist, fehlt (außer den Stationen). */
export function besichtigungZahlen(a: Pick<AkteAufnahme, "stationenFertig" | "stationenGesamt" | "fotos" | "notizen" | "memos" | "laengen">): string {
  const teile = [`${a.stationenFertig} von ${a.stationenGesamt} Stationen fertig`];
  if (a.fotos > 0) teile.push(anzahl(a.fotos, "Foto", "Fotos"));
  if (a.notizen > 0) teile.push(anzahl(a.notizen, "Notiz", "Notizen"));
  if (a.memos > 0) teile.push(anzahl(a.memos, "Sprachmemo", "Sprachmemos"));
  if (a.laengen > 0) teile.push(anzahl(a.laengen, "Länge", "Längen"));
  return teile.join(" · ");
}
